import {
  Query,
  ResultSet,
  areQueriesEqual,
  isQueryPresent,
} from '@cubejs-client/core';
import { CubeContext, UseCubeQueryOptions } from '@cubejs-client/react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import useDeepCompareMemoize from '@cubejs-client/react/src/hooks/deep-compare-memoize';
import React from 'react';

const CUBE_MAX_LIMIT = 50000;

export interface CubePaginationOptions {
  offset: number;
  limit: number;
}

interface QueryOptions extends UseCubeQueryOptions {
  paginationOptions?: CubePaginationOptions;
}

export const useCubeQueryChunkLoadingSync = (
  dataQuery: Query,
  options: QueryOptions
) => {
  const mutexRef = React.useRef({});
  const [chunks, setChunks] = React.useState<ResultSet[]>([]);
  const [error, setError] = React.useState<Error | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const context = React.useContext(CubeContext);
  const [currentQuery, setCurrentQuery] = React.useState<Query | null>(null);

  const fetchData = async () => {
    setChunks([]);
    setIsLoading(true);
    let lastResultSetRowsCount: number | null = null;
    let offset = 0;

    const fetchedChunks: ResultSet[] = [];
    const MAX_ITERATIONS = 10; // Prevent infinite loops
    let iterations = 0;

    try {
      if (options.paginationOptions) {
        // Single fetch for normal pagination
        const offset = options.paginationOptions.offset ?? 0;
        const limit = options.paginationOptions.limit ?? 100;

        const resultSet = await (options?.cubejsApi || context.cubejsApi).load(
          {
            ...dataQuery,
            offset,
            limit,
          },
          {
            mutexObj: mutexRef.current,
            mutexKey: `query`,
          }
        );

        setChunks([resultSet]);
      } else {
        // Fetch all, paginate by CUBE_MAX_LIMIT if necessary
        while (
          (lastResultSetRowsCount === null ||
            lastResultSetRowsCount >= CUBE_MAX_LIMIT) &&
          iterations < MAX_ITERATIONS
        ) {
          const resultSet = await (
            options?.cubejsApi || context.cubejsApi
          ).load(
            { ...dataQuery, offset, limit: CUBE_MAX_LIMIT },
            {
              mutexObj: mutexRef.current,
              mutexKey: `query`,
            }
          );
          lastResultSetRowsCount = resultSet.rawData().length;
          fetchedChunks.push(resultSet);

          // Correctly increment the offset
          offset += lastResultSetRowsCount;

          iterations += 1;

          if (lastResultSetRowsCount < CUBE_MAX_LIMIT) {
            break; // No more data to fetch
          }

          if (iterations >= MAX_ITERATIONS) {
            console.warn('Maximum iterations reached. Exiting fetch loop.');
            break;
          }
        }

        setChunks(fetchedChunks);
      }
    } catch (e) {
      console.error(e);
      setError(e as Error);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    const { skip = false } = options;

    const cubejsApi = options.cubejsApi || context?.cubejsApi;

    if (!cubejsApi) {
      throw new Error('Cube.js API client is not provided');
    }

    async function loadQuery() {
      if (!skip && isQueryPresent(dataQuery)) {
        if (!areQueriesEqual(currentQuery, dataQuery)) {
          setCurrentQuery(dataQuery);
        }

        setError(null);

        fetchData();
      }
    }

    loadQuery();
  }, useDeepCompareMemoize([dataQuery, Object.keys((dataQuery && dataQuery.order) || {}), options, context]));

  const dataResult = React.useMemo(() => {
    if (error) {
      return null;
    }
    const referenceResultSet = chunks?.[0];
    if (referenceResultSet) {
      // Reconstruct result set based on new dataset
      const serializedResultSet = referenceResultSet?.serialize();

      serializedResultSet.loadResponse.results[0].data = [
        ...chunks.map((el) => el.rawData()).flat(),
      ];

      const resultSet = ResultSet.deserialize(serializedResultSet);
      return resultSet;
    } else {
      return null;
    }
  }, [chunks]);

  const result = React.useMemo(() => {
    if (options.skip) {
      return { resultSet: dataResult, error: error, isLoading: false };
    }
    return { resultSet: dataResult, error: error, isLoading };
  }, [dataResult, error, isLoading, options]);

  return result;
};
